import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import {
  ActionInput,
  AgendaItemInput,
  ItemKind,
  Majority,
  type AgendaItem,
  type Meeting,
  type ProposedAgendaItem,
  type UpdateAgendaItem,
  type UpdateMeeting,
} from '@syndiqia/governance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../../components/ui';
import { meetingWhen, useGovernanceMutation, useMeeting, useNotice, type AgendaDraft } from '../../governance-queries';
import { useI18n } from '../../i18n';
import { RecordingPanel } from './Recorder';
import { AttendancePanel, VotePanel } from './Votes';
import { useSyndicate } from '../../session';

type Step = 'agenda' | 'notice' | 'meeting' | 'minutes';

export function MeetingPage() {
  const { t, locale } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const { meetingId = '' } = useParams();
  const meeting = useMeeting(meetingId);
  const [step, setStep] = useState<Step | null>(null);

  if (!s.can('governance.read')) return <Alert tone="info">{g.boardOnly}</Alert>;
  if (meeting.isPending) return <Loading label={t.loading} />;
  if (meeting.isError) return <Alert>{errorMessage(meeting.error, t)}</Alert>;
  const m = meeting.data;
  const current: Step = step ?? (m.status === 'draft' ? 'agenda' : m.status === 'convened' ? 'meeting' : 'minutes');

  return (
    <>
      <Link to={`${s.base}/governance`} className="back">
        ← {g.back}
      </Link>
      <PageHeader title={m.title} intro={`${meetingWhen(m.scheduledAt, locale)}${m.location ? ` · ${m.location}` : ''}`} />
      <p>
        <span className={`badge badge-meeting-${m.kind}`}>{g.kinds[m.kind]}</span> <span className={`badge badge-mstatus-${m.status}`}>{g.statuses[m.status]}</span>
      </p>
      <nav className="subnav steps" aria-label={m.title}>
        {(['agenda', 'notice', 'meeting', 'minutes'] as const).map((key, i) => (
          <button key={key} type="button" className={current === key ? 'active' : undefined} onClick={() => setStep(key)}>
            {i + 1}. {g.steps[key]}
          </button>
        ))}
      </nav>
      {current === 'agenda' && <AgendaStep meeting={m} />}
      {current === 'notice' && <NoticeStep meeting={m} />}
      {current === 'meeting' && <MeetingStep meeting={m} />}
      {current === 'minutes' && <MinutesStep meeting={m} />}
    </>
  );
}

// --- 1. Agenda

function AgendaStep({ meeting }: { meeting: Meeting }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const editable = s.can('governance.manage') && meeting.status !== 'closed' && meeting.status !== 'cancelled';
  return (
    <>
      <p className="muted">{g.agendaHint}</p>
      {meeting.status === 'convened' && <Alert tone="info">{g.convenedEdit}</Alert>}
      <ol className="agenda">
        {meeting.items.map((item, i) => (
          <AgendaRow key={item.id} item={item} first={i === 0} last={i === meeting.items.length - 1} editable={editable} />
        ))}
      </ol>
      {editable && (
        <div className="form-actions">
          <Disclosure label={g.addItem}>{(close) => <ItemForm meetingId={meeting.id} onDone={close} />}</Disclosure>
          <AgentProposals meeting={meeting} />
        </div>
      )}
    </>
  );
}

function AgendaRow({ item, first, last, editable }: { item: AgendaItem; first: boolean; last: boolean; editable: boolean }) {
  const { t } = useI18n();
  const g = t.governance;
  const [editing, setEditing] = useState(false);
  const move = useGovernanceMutation<'up' | 'down', void>((api, base, direction) => api(`${base}/governance/items/${item.id}/move`, { method: 'POST', body: { direction } }));
  const remove = useGovernanceMutation<void, void>((api, base) => api(`${base}/governance/items/${item.id}`, { method: 'DELETE' }));

  if (editing) return <li className="agenda-item">{<ItemForm item={item} onDone={() => setEditing(false)} />}</li>;
  return (
    <li className="agenda-item">
      <div className="agenda-main">
        <strong>{item.title}</strong>
        <span className="agenda-tags">
          <span className={`badge badge-item-${item.kind}`}>{g.itemKinds[item.kind]}</span>
          {item.majority && <span className="badge">{g.majorities[item.majority]}</span>}
          {item.source !== 'statutory' && item.source !== 'manual' && <span className="badge badge-source">{g.sources[item.source]}</span>}
        </span>
        {item.description && <p className="muted small">{item.description}</p>}
        {item.proposedResolution && <p className="resolution small">{item.proposedResolution}</p>}
      </div>
      {editable && (
        <div className="agenda-actions">
          <button type="button" className="link-button small" disabled={first || move.isPending} onClick={() => move.mutate('up')} aria-label={g.up}>
            ↑
          </button>
          <button type="button" className="link-button small" disabled={last || move.isPending} onClick={() => move.mutate('down')} aria-label={g.down}>
            ↓
          </button>
          <button type="button" className="link-button small" onClick={() => setEditing(true)}>
            {g.edit}
          </button>
          <button type="button" className="link-button small" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {g.remove}
          </button>
        </div>
      )}
    </li>
  );
}

function ItemForm({ meetingId, item, onDone }: { meetingId?: string; item?: AgendaItem; onDone: () => void }) {
  const { t } = useI18n();
  const g = t.governance;
  const [values, setValues] = useState({
    title: item?.title ?? '',
    description: item?.description ?? '',
    kind: item?.kind ?? 'discussion',
    proposedResolution: item?.proposedResolution ?? '',
    majority: item?.majority ?? 'simple',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useGovernanceMutation<AgendaItemInput | UpdateAgendaItem, AgendaItem>((api, base, input) =>
    item ? api(`${base}/governance/items/${item.id}`, { method: 'PATCH', body: input }) : api(`${base}/governance/meetings/${meetingId}/items`, { method: 'POST', body: input }),
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const isResolution = values.kind === 'resolution';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const input = { ...values, proposedResolution: isResolution ? values.proposedResolution : '', majority: isResolution ? values.majority : undefined };
    const result = validate(AgendaItemInput, input);
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(item ? { ...result.data, majority: result.data.majority ?? null } : result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={g.itemTitle} error={errors.title}>
          <input value={values.title} onChange={set('title')} autoFocus />
        </Field>
        <Field label={g.itemKind}>
          <select value={values.kind} onChange={set('kind')}>
            {ItemKind.options.map((k) => (
              <option key={k} value={k}>
                {g.itemKinds[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={g.description} hint={t.common.optional}>
        <textarea rows={2} value={values.description} onChange={set('description')} />
      </Field>
      {isResolution && (
        <div className="row">
          <Field label={g.resolution} hint={t.common.optional}>
            <textarea rows={2} value={values.proposedResolution} onChange={set('proposedResolution')} placeholder="Il est résolu…" />
          </Field>
          <Field label={g.majority}>
            <select value={values.majority} onChange={set('majority')}>
              {Majority.options.map((k) => (
                <option key={k} value={k}>
                  {g.majorities[k]}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

function AgentProposals({ meeting }: { meeting: Meeting }) {
  const { t } = useI18n();
  const g = t.governance;
  const draft = useGovernanceMutation<void, AgendaDraft>((api, base) => api(`${base}/governance/meetings/${meeting.id}/agenda-draft`, { method: 'POST' }));
  const add = useGovernanceMutation<ProposedAgendaItem, AgendaItem>((api, base, { reason: _reason, ...input }) => api(`${base}/governance/meetings/${meeting.id}/items`, { method: 'POST', body: input }));
  const onAgenda = new Set(meeting.items.map((i) => i.sourceId));

  if (!draft.data && !draft.isPending && !draft.isError) {
    return (
      <button type="button" className="button button-agent" onClick={() => draft.mutate()}>
        ✦ {g.draftAgenda}
      </button>
    );
  }
  return (
    <section className="panel suggestion-panel agenda-proposals">
      <h2>✦ {g.proposalsTitle}</h2>
      {draft.isPending && <Loading label={g.draftingAgenda} />}
      {draft.isError && <Alert>{errorMessage(draft.error, t)}</Alert>}
      {draft.data && (
        <>
          <p className="muted small">{g.proposalsHint}</p>
          {draft.data.proposals.length === 0 && <Empty>{g.noProposals}</Empty>}
          <ul className="proposal-list">
            {draft.data.proposals.map((p) => {
              const added = !!p.sourceId && onAgenda.has(p.sourceId);
              return (
                <li key={`${p.source}-${p.sourceId}`}>
                  <div>
                    <strong>{p.title}</strong> <span className={`badge badge-item-${p.kind}`}>{g.itemKinds[p.kind ?? 'discussion']}</span>{' '}
                    <span className="badge badge-source">{g.sources[p.source ?? 'manual']}</span>
                    {p.description && <p className="small">{p.description}</p>}
                    {p.proposedResolution && <p className="resolution small">{p.proposedResolution}</p>}
                    <p className="muted small">{p.reason}</p>
                  </div>
                  <button type="button" className="button button-small" disabled={added || add.isPending} onClick={() => add.mutate(p)}>
                    {added ? `✓ ${g.added}` : g.add}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

// --- 2. Notice of meeting

function NoticeStep({ meeting }: { meeting: Meeting }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const notice = useNotice(meeting.id);
  const prepare = useGovernanceMutation<void, unknown>((api, base) => api(`${base}/governance/meetings/${meeting.id}/notice`, { method: 'POST' }));
  const decide = useGovernanceMutation<{ id: string; outcome: 'approved' | 'rejected' }, unknown>((api, base, { id, outcome }) => api(`${base}/approvals/${id}/decide`, { method: 'POST', body: { outcome } }));
  const n = notice.data;
  const rule = meeting.notice;

  return (
    <>
      <div className="panel">
        <p>
          {g.noticeRule(rule.minDays, rule.maxDays)} {g.noticeDays(rule.daysBefore)}
        </p>
        {!rule.withinPeriod && meeting.status === 'draft' && <Alert tone="info">{g.outsidePeriod}</Alert>}
        {rule.minDays !== null && <p className="muted small">{g.ruleToValidate}</p>}
      </div>
      {notice.isPending && <Loading label={t.loading} />}
      {n?.status === 'approved' && <Alert tone="info">{g.noticeSent(n.decision?.result?.sent ?? n.payload.items.length, date((n.decidedAt ?? n.createdAt).slice(0, 10)))}</Alert>}
      {n?.status === 'rejected' && <Alert tone="info">{g.noticeRejected}</Alert>}
      {(!n || n.status === 'rejected') && meeting.status === 'draft' && s.can('governance.manage') && (
        <div className="form-actions">
          <button type="button" className="button button-primary" disabled={prepare.isPending} onClick={() => prepare.mutate()}>
            {g.prepareNotice}
          </button>
        </div>
      )}
      {prepare.isError && <Alert>{errorMessage(prepare.error, t)}</Alert>}
      {n?.status === 'pending' && (
        <section className="panel">
          <h2>{g.noticePending}</h2>
          <p>{g.noticeRecipients(n.payload.items.length)}</p>
          {n.payload.unreachable.length > 0 && <Alert tone="info">{g.noticeUnreachable(n.payload.unreachable.length)} {n.payload.unreachable.map((u) => u.name).join(', ')}</Alert>}
          <details>
            <summary>{g.preview}</summary>
            <p className="small">
              <strong>{n.payload.items[0]?.subject}</strong>
            </p>
            <p className="message-body small">{n.payload.items[0]?.text}</p>
          </details>
          <details>
            <summary>{g.noticeRecipients(n.payload.items.length)}</summary>
            <p className="small muted">{n.payload.items.map((i) => `${i.name} <${i.email}>`).join(', ')}</p>
          </details>
          {decide.isError && <Alert>{errorMessage(decide.error, t)}</Alert>}
          {s.can('governance.approve') && (
            <div className="form-actions">
              <button type="button" className="button button-primary" disabled={decide.isPending} onClick={() => decide.mutate({ id: n.id, outcome: 'approved' })}>
                {g.approveSend(n.payload.items.length)}
              </button>
              <button type="button" className="button" disabled={decide.isPending} onClick={() => decide.mutate({ id: n.id, outcome: 'rejected' })}>
                {g.reject}
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

// --- 3. The meeting: notes per item

function MeetingStep({ meeting }: { meeting: Meeting }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const editable = s.can('governance.manage') && meeting.status !== 'closed' && meeting.status !== 'cancelled';
  const update = useGovernanceMutation<UpdateMeeting, Meeting>((api, base, input) => api(`${base}/governance/meetings/${meeting.id}`, { method: 'PATCH', body: input }));

  return (
    <>
      <AttendancePanel meeting={meeting} editable={editable} />
      <RecordingPanel meeting={meeting} editable={editable} />
      <div className="panel">
        <AutoSaveArea label={g.generalNotes} placeholder={g.generalNotesHint} value={meeting.generalNotes} disabled={!editable} onSave={(text) => update.mutateAsync({ generalNotes: text })} />
      </div>
      <ol className="agenda agenda-notes">
        {meeting.items.map((item) => (
          <ItemNotes key={item.id} meeting={meeting} item={item} editable={editable} />
        ))}
      </ol>
      {update.isError && <Alert>{errorMessage(update.error, t)}</Alert>}
      {editable && (meeting.status === 'draft' || meeting.status === 'convened') && (
        <div className="form-actions">
          <button type="button" className="button button-primary" disabled={update.isPending} onClick={() => update.mutate({ status: 'held' })}>
            {g.markHeld}
          </button>
          <button type="button" className="button" disabled={update.isPending} onClick={() => update.mutate({ status: 'cancelled' })}>
            {g.cancelMeeting}
          </button>
        </div>
      )}
    </>
  );
}

function ItemNotes({ meeting, item, editable }: { meeting: Meeting; item: AgendaItem; editable: boolean }) {
  const { t } = useI18n();
  const g = t.governance;
  const update = useGovernanceMutation<UpdateAgendaItem, AgendaItem>((api, base, input) => api(`${base}/governance/items/${item.id}`, { method: 'PATCH', body: input }));
  return (
    <li className="agenda-item agenda-item-notes">
      <div className="agenda-main">
        <strong>{item.title}</strong>
        {item.proposedResolution && <p className="resolution small">{item.proposedResolution}</p>}
        <AutoSaveArea label={g.notes} value={item.notes} disabled={!editable} onSave={(text) => update.mutateAsync({ notes: text })} />
        {(item.kind === 'resolution' || item.kind === 'election') && <VotePanel meeting={meeting} item={item} editable={editable} />}
        {(item.kind === 'resolution' || item.kind === 'election' || item.decision) && (
          <AutoSaveArea label={g.decision} rows={1} placeholder={g.decisionPlaceholder} value={item.decision} disabled={!editable} onSave={(text) => update.mutateAsync({ decision: text })} />
        )}
      </div>
    </li>
  );
}

/** A text area saved when it loses focus (meeting notes are typed live). */
function AutoSaveArea({ label, value, onSave, placeholder, rows = 3, disabled }: { label: string; value: string; onSave: (text: string) => Promise<unknown>; placeholder?: string; rows?: number; disabled?: boolean }) {
  const { t } = useI18n();
  const [text, setText] = useState(value);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  useEffect(() => setText(value), [value]);
  const save = async () => {
    if (text === value) return;
    setState('saving');
    try {
      await onSave(text);
      setState('saved');
    } catch {
      setState('error');
    }
  };
  return (
    <Field label={label} hint={state === 'saved' ? `✓ ${t.governance.saved}` : undefined} error={state === 'error' ? t.common.error : undefined}>
      <textarea rows={Math.max(rows, text.split('\n').length)} value={text} placeholder={placeholder} disabled={disabled} onChange={(e) => (setText(e.target.value), setState('idle'))} onBlur={save} />
    </Field>
  );
}

// --- 4. Minutes and follow-ups

function MinutesStep({ meeting }: { meeting: Meeting }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const approved = meeting.minutesStatus === 'approved';
  const editable = s.can('governance.manage') && !approved && meeting.status !== 'cancelled';
  const draft = useGovernanceMutation<void, Meeting>((api, base) => api(`${base}/governance/meetings/${meeting.id}/minutes-draft`, { method: 'POST' }));
  const update = useGovernanceMutation<UpdateMeeting, Meeting>((api, base, input) => api(`${base}/governance/meetings/${meeting.id}`, { method: 'PATCH', body: input }));
  const approve = useGovernanceMutation<void, Meeting>((api, base) => api(`${base}/governance/meetings/${meeting.id}/minutes/approve`, { method: 'POST' }));

  return (
    <>
      {approved && meeting.minutesApprovedAt && (
        <Alert tone="info">
          {g.approvedOn(date(meeting.minutesApprovedAt.slice(0, 10)))}
          {meeting.minutesDocumentId && (
            <>
              {' '}
              {g.publishedNote} <Link to={`${s.base}/documents`}>{g.seeDocument}</Link>
            </>
          )}
        </Alert>
      )}
      {editable && (
        <div className="form-actions">
          <button type="button" className="button button-agent" disabled={draft.isPending} onClick={() => draft.mutate()}>
            ✦ {meeting.minutes ? g.redraftMinutes : g.draftMinutes}
          </button>
        </div>
      )}
      {draft.isPending && <Loading label={g.draftingMinutes} />}
      {draft.isError && <Alert>{errorMessage(draft.error, t)}</Alert>}
      {(meeting.minutes || editable) && (
        <div className="panel">
          {editable && <p className="muted small">{g.minutesHint}</p>}
          {approved ? (
            <pre className="minutes">{meeting.minutes}</pre>
          ) : (
            <AutoSaveArea key={meeting.minutes.length} label={g.minutesText} rows={16} value={meeting.minutes} disabled={!editable} onSave={(text) => update.mutateAsync({ minutes: text })} />
          )}
          {approve.isError && <Alert>{errorMessage(approve.error, t)}</Alert>}
          {!approved && meeting.minutes.trim() && s.can('governance.approve') && (
            <div className="form-actions">
              <button type="button" className="button button-primary" disabled={approve.isPending} onClick={() => approve.mutate()}>
                {g.approveMinutes}
              </button>
            </div>
          )}
        </div>
      )}
      <Actions meeting={meeting} />
    </>
  );
}

function Actions({ meeting }: { meeting: Meeting }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const setStatus = useGovernanceMutation<{ id: string; status: 'open' | 'done' }, void>((api, base, { id, status }) => api(`${base}/governance/actions/${id}/status`, { method: 'POST', body: { status } }));
  const manages = s.can('governance.manage');
  return (
    <section className="panel">
      <h2>{g.actions}</h2>
      <p className="muted small">{g.actionsHint}</p>
      {meeting.actions.length === 0 && <Empty>{g.noActions}</Empty>}
      <ul className="action-list">
        {meeting.actions.map((a) => (
          <li key={a.id} className={a.status === 'done' ? 'action-done' : undefined}>
            <span>{a.description}</span>
            <span className="muted small">{[a.responsible, a.dueOn && date(a.dueOn)].filter(Boolean).join(' · ')}</span>
            {manages && (
              <button type="button" className="link-button small" onClick={() => setStatus.mutate({ id: a.id, status: a.status === 'done' ? 'open' : 'done' })}>
                {a.status === 'done' ? g.reopen : `✓ ${g.done}`}
              </button>
            )}
          </li>
        ))}
      </ul>
      {manages && <Disclosure label={g.addAction}>{(close) => <ActionForm meetingId={meeting.id} onDone={close} />}</Disclosure>}
    </section>
  );
}

function ActionForm({ meetingId, onDone }: { meetingId: string; onDone: () => void }) {
  const { t } = useI18n();
  const g = t.governance;
  const [values, setValues] = useState({ description: '', responsible: '', dueOn: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const add = useGovernanceMutation<ActionInput, unknown>((api, base, input) => api(`${base}/governance/meetings/${meetingId}/actions`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(ActionInput, values);
    setErrors(result.errors ?? {});
    if (result.data) add.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <Field label={g.actionDescription} error={errors.description}>
        <input value={values.description} onChange={set('description')} autoFocus />
      </Field>
      <div className="row">
        <Field label={g.responsible} hint={t.common.optional}>
          <input value={values.responsible} onChange={set('responsible')} />
        </Field>
        <Field label={g.dueOn} hint={t.common.optional} error={errors.dueOn}>
          <input type="date" value={values.dueOn} onChange={set('dueOn')} />
        </Field>
      </div>
      {add.isError && <Alert>{errorMessage(add.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={add.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
